// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import Contact from '../../models/Contact';
import FundOpportunity from '../../models/FundOpportunity';
export const adminBackendServiceOperationsMixin = {
  async getReferralInvitationDetails(id: string): Promise<{
    data: {
      invitation: {
        id: string;
        inviterContactId: number;
        inviteeEmail: string;
        inviteeFirstName: string;
        inviteeLastName: string;
        invitationLink: string;
        invitationCode: string;
        invitationExpiry: string;
        status: string;
        rejectionReason: string | null;
        opportunityId: number | null;
        eoiAmount: number | null;
        inviteeContactId: number | null;
        createdAt: string;
        updatedAt: string;
        expired: boolean;
        expiresIn: number;
      };
      inviterContact: Contact;
      fundOpportunity: FundOpportunity | null;
      inviterInvestor: {
        id: string;
        contactId: string;
        fundOpportunityId: string;
        status: string;
        [key: string]: unknown;
      } | null;
    };
  }> {
    return this.authApiGetAuthed(
      `/api/referral_invitations/${id}`,
      'Network error while fetching referral invitation details',
      (json) => json,
      true,
    );
  },

  listFundEventsForFund(fundOpportunityExternalId: string): Promise<{ events: unknown[] }> {
    return this.authApiGetAuthed(
      `admin/fund-events?fundOpportunityExternalId=${fundOpportunityExternalId}&page=1&limit=500`,
      'Network error while fetching fund events',
      (json: { data?: { items?: unknown[] } }) => ({
        events: Array.isArray(json?.data?.items) ? json.data!.items! : [],
      }),
    );
  },

  createFundEvent(body: {
    fundOpportunityExternalId: string;
    eventType: string;
    eventDate: string;
    amount?: number | null;
    notes?: string | null;
  }): Promise<unknown> {
    return this.authApiMutateAuthed('admin/fund-events', 'POST', body, (json) => json);
  },

  updateFundEvent(
    externalId: string,
    body: {
      eventType?: string;
      eventDate?: string;
      amount?: number | null;
      notes?: string | null;
    },
  ): Promise<unknown> {
    return this.authApiMutateAuthed(`admin/fund-events/${externalId}`, 'PUT', body, (json) => json);
  },

  deleteFundEvent(externalId: string): Promise<unknown> {
    return this.authApiMutateAuthed(
      `admin/fund-events/${externalId}`,
      'DELETE',
      undefined,
      (json) => json,
    );
  },

  getAdminFundUpdates(fundOpportunityId: string): Promise<AdminFundUpdateEntry[]> {
    return this.authApiGetAuthed(
      `admin/fundOpportunities/${fundOpportunityId}/updates`,
      'Network error while fetching fund updates',
      (json) => {
        console.log('[getAdminFundUpdates] raw response:', JSON.stringify(json.data));
        return json.data as AdminFundUpdateEntry[];
      },
    );
  },

  createFundUpdate(
    fundOpportunityId: string,
    updateDate?: string,
    title?: string | null,
  ): Promise<AdminFundUpdateEntry> {
    return this.authApiMutateAuthed(
      `admin/fundOpportunities/${fundOpportunityId}/updates`,
      'POST',
      { ...(updateDate ? { updateDate } : {}), ...(title ? { title } : {}) },
      (json) => json.data as AdminFundUpdateEntry,
    );
  },

  setFundUpdateDate(
    fundOpportunityId: string,
    updateId: string,
    updateDate: string,
  ): Promise<AdminFundUpdateEntry> {
    return this.authApiMutateAuthed(
      `admin/fundOpportunities/${fundOpportunityId}/updates/${updateId}/date`,
      'PATCH',
      { updateDate },
      (json) => json.data as AdminFundUpdateEntry,
    );
  },

  /** Set or clear (null/blank) an update's title. Works on drafts and published updates. */
  setFundUpdateTitle(
    fundOpportunityId: string,
    updateId: string,
    title: string | null,
  ): Promise<{ externalId: string; title: string | null }> {
    return this.authApiMutateAuthed(
      `admin/fundOpportunities/${fundOpportunityId}/updates/${updateId}/title`,
      'PATCH',
      { title },
      (json) => json.data as { externalId: string; title: string | null },
    );
  },

  publishFundUpdate(fundOpportunityId: string, updateId: string): Promise<AdminFundUpdateEntry> {
    return this.authApiMutateAuthed(
      `admin/fundOpportunities/${fundOpportunityId}/updates/${updateId}/publish`,
      'POST',
      undefined,
      (json) => json.data as AdminFundUpdateEntry,
    );
  },

  discardFundUpdate(
    fundOpportunityId: string,
    updateId: string,
    options?: { confirm?: boolean },
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `admin/fundOpportunities/${fundOpportunityId}/updates/${updateId}`,
      'DELETE',
      options,
      () => undefined,
    );
  },

  setFundUpdateHighlight(
    fundOpportunityId: string,
    updateId: string,
    updateCard: {
      title: string;
      summary: string;
      imageUrl?: string | null;
      titleOverridden?: boolean;
    } | null,
  ): Promise<AdminFundUpdateEntry> {
    return this.authApiMutateAuthed(
      `admin/fundOpportunities/${fundOpportunityId}/updates/${updateId}/highlight`,
      'PATCH',
      { updateCard },
      (json) => json.data as AdminFundUpdateEntry,
    );
  },

  generateFundUpdateHighlight(
    fundOpportunityId: string,
    updateId: string,
    additionalPrompt?: string,
    useProModel?: boolean,
    currentCard?: { title: string; summary: string; imageUrl?: string | null },
  ): Promise<{
    title: string;
    summary: string;
    imageUrl: string | null;
    availableImages: string[];
  }> {
    const body: Record<string, unknown> = {};
    if (additionalPrompt) body.additionalPrompt = additionalPrompt;
    if (useProModel) body.useProModel = true;
    if (currentCard) body.currentCard = currentCard;
    return this.authApiMutateAuthed(
      `admin/fundOpportunities/${fundOpportunityId}/updates/${updateId}/highlight/generate`,
      'POST',
      body,
      (json) => json.data,
    );
  },

  listAdminNotes(
    modelType: string,
    modelExternalId: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<{ data: AdminNoteItem[] }> {
    const params = new URLSearchParams({
      modelType,
      modelExternalId,
      limit: String(options.limit ?? 10),
      offset: String(options.offset ?? 0),
    });
    return this.authApiGetAuthed(
      `admin/notes?${params}`,
      'Network error while fetching admin notes',
      (json) => json,
    );
  },

  createAdminNote(input: {
    modelType: string;
    modelExternalId: string;
    content: string;
  }): Promise<{ data: { externalId: string } }> {
    return this.authApiMutateAuthed('admin/notes', 'POST', input, (json) => json);
  },

  updateAdminNote(externalId: string, content: string): Promise<{ data: { externalId: string } }> {
    return this.authApiMutateAuthed(
      `admin/notes/${externalId}`,
      'PATCH',
      { content },
      (json) => json,
    );
  },

  deleteAdminNote(externalId: string): Promise<void> {
    return this.authApiDeleteVoid(`admin/notes/${externalId}`);
  },
} satisfies ThisType<BaseBackendService>;
